import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, ImageOff, Check, AlertTriangle, Maximize2, ChevronDown } from 'lucide-react';
import chatService, { SectionDiagram, CandidateSection } from '../../../services/ChatService';
import { useActiveSessionIdSafe } from '../../../contexts/SessionContext';
import { useOrder } from '../../../contexts/OrderContext';
import { TranslationBatchManager } from '../../../services/TranslationService';
import { ClarificationQuestion, PartRefLite, ClarificationHint, ClarificationHintState } from './types';
import { HintBubble, HintCandidateNote, HintComparisonStrip, rowHasHintDifference, resolveCandidate, aiRowTagsOf } from './ClarificationHintPanel';
import { refinedRequestLabel } from './narrowingLabel';
import { answerOptionsFrom, requestedTypeOf, isCatchAllAnswer, AiRowTags } from '../../../utils/partTags';
import { DIAGRAM_PALETTE, CalloutPriority } from '../../../utils/diagramTag';
import { positionChipsFromExtra, positionClassesFromName, displayPartRef } from '../../../utils/partLabel';
import { pollRefinedHint } from './hintRefinement';
import { collapseSupersededPartRows } from '../../../utils/partSupersession';
import DiagramModal, { DiagramRow, DiagramHeaderCtx, diagramTexts } from '../../common/DiagramModal';
import { useOrgFeatureFlag } from '../../../hooks/useOrgFeatureFlag';
import { isCatalogKit } from '../../../utils/catalogKits';

// ── Inline clarification diagram ────────────────────────────────────────────
// Rendered directly inside the clarification form: a preview of the section's
// exploded view (or a two-up grid when several sections answer the question)
// that opens the shared DiagramModal. Each option carries read-only `load`
// refs (group_id, section_id, partId, callout); we group them by section,
// fetch each section's diagram on demand (GET /api/chat/section-diagram) and
// resolve every catalog part on it to a choice: a suggested option, a concrete
// pick (has a real partId), or a free-text pick (escape hatch). View-only once
// the form is locked. The modal itself — tags, legend, parts list, zoom — is
// the same component the search results use.

interface Props {
  question: ClarificationQuestion;
  t?: (key: string, opts?: any) => string;
  /** 'single_select' picks one, 'multi_select' toggles. */
  questionType: ClarificationQuestion['question_type'];
  /** Option ids currently selected for this question (reflected in the diagram). */
  selectedOptionIds: string[];
  /** Toggle an option from the diagram. */
  onToggleOption: (optionId: string) => void;
  /**
   * Pick a non-related part as the free-text answer (the "type your own" path).
   * `partKey` is the clicked part's UNIQUE row key — stored so only THAT part
   * highlights, not every row that happens to share the same catalog name.
   */
  onSelectOther: (text: string, partKey?: string) => void;
  /** Clear the free-text ("type your own") pick — clicking a chosen non-related part again. */
  onClearOther: () => void;
  /**
   * partIds the user has picked DIRECTLY off the diagram (concrete catalog parts
   * that aren't a suggested option). These are loaded as-is on the answer turn —
   * the fast-path — instead of going through free-text re-search.
   */
  pickedPartIds?: string[];
  /**
   * Parts already chosen in the OTHER questions of the same clarification form.
   * When one of them lives on a diagram this question also shows, it renders
   * with an "already selected" ✓ badge on the tag and leaves the list.
   */
  priorPicks?: PartRefLite[];
  /** Pick a concrete catalog part clicked on the diagram (has a real partId). */
  onPickPart: (ref: PartRefLite) => void;
  /** Un-pick a concrete catalog part (clicking it again). */
  onUnpickPart: (partId: string) => void;
  /** @deprecated The viewer no longer offers the decline (see onCantFind); accepted so callers need no change. */
  onSelectNone?: () => void;
  noneSelected?: boolean;
  noneLabel?: string;
  /** True when the free-text ("type your own") choice is currently selected. */
  otherSelected?: boolean;
  /** Current free-text value — used to highlight a non-related part already picked. */
  otherText?: string;
  /**
   * Unique row key of the non-related part picked this session (from a diagram
   * click). When set it pins the "selected" highlight to exactly that part.
   * Absent on a typed answer or a reloaded form — then we fall back to matching
   * by catalog name.
   */
  otherKey?: string;
  /** When locked (already-submitted form) the diagram is view-only. */
  disabled?: boolean;
  /**
   * Confirm the picks made on the big map: closes the modal AND answers the
   * question (the form advances to the next question, or submits on the last
   * one) — so the user doesn't need a second Next/Submit click.
   */
  onConfirm?: () => void;
  /** Gates the modal's Confirm button (mirrors the form's Next/Submit gating). */
  confirmDisabled?: boolean;
  /** Drop every pick on this question (options, diagram parts, a typed part).
   *  Without it the viewer unpicks them one by one. */
  onClearSelection?: () => void;
  /** "Can't find it?" in the parts list's empty state: the viewer closes and
   *  the question's chat opens to describe the part. */
  onCantFind?: () => void;
  /**
   * The FULL resolved header label for this question — the same one the inline
   * card shows. The fullscreen map header uses it so the map carries the
   * complete question, not the short part name.
   */
  headerLabel?: string;
  /**
   * AI hint, offered from the modal header. Called with a copy of the question
   * whose options are ONLY the candidates on the open diagram, each carrying
   * that diagram's OE part number. Absent = no button (guest pages, locked
   * form, no options).
   */
  onRequestHint?: (question: ClarificationQuestion) => Promise<ClarificationHint>;
  /**
   * Fullscreen answering flow. When the user confirms a question INSIDE the
   * fullscreen map, the form advances and the next question's map opens
   * fullscreen straight away (`openFullscreenOnMount`). `onFullscreenChange`
   * tells the form when the user opens or leaves the map.
   */
  openFullscreenOnMount?: boolean;
  onFullscreenChange?: (open: boolean) => void;
  /** An answer chip was clicked on the card: open the map on that option's
   *  section (the option is already selected upstream). `nonce` changes per
   *  click so the same chip reopens the map after it was closed. */
  openRequest?: { optionId: string; nonce: number } | null;
  /** True while an `openRequest` waits for its drawing to load (the viewer
   *  opens only once the image is ready); the caller shows a spinner. */
  onOpeningChange?: (opening: boolean) => void;
  /** The request line this question is about — the customer's words and our
   *  reading — shown in the modal strip. */
  requestLine?: { original: string; label: string } | null;
  /** The question exactly as the chat card shows it — its text and numbered
   *  answers (title, part count, the grey line). The viewer's question block
   *  shows the same. `optionIds` are this map's options each answer covers;
   *  `tagged` = the answers group parts (a narrowing question), so each part
   *  wears its answer's number. */
  displayQuestion?: {
    text: string;
    answers: Array<{ n: number; title: string; line?: string | null; count?: number | null; optionIds: string[] }>;
    tagged?: boolean;
  } | null;
  /** The question was already answered: the map opens to REVIEW the pick —
   *  green strip with a resolved chip, no Confirm, no "can't find it". */
  answered?: boolean;
  /** Stepper context for the modal footer: progress + Back; Confirm keeps the
   *  map open until the last question. Absent for a single question. */
  stepper?: { current: number; total: number; isLast: boolean; canGoBack: boolean; onBack: () => void };
  /** Footer confirm label — the form's own Next / Submit wording. */
  confirmLabel?: string;
}

interface SectionRef {
  group_id: string;
  section_id: string;
  /** Best-scored option's part in this section — picks the illustration page to draw. */
  part_id?: string;
}

type DiagramPart = SectionDiagram['parts'][number];
/** What a row hands back through DiagramModal.onToggleRow. */
type RowRaw = { part: DiagramPart; optionId?: string; ambiguous: boolean; promoted?: boolean };

/** Kit SKUs and their components are real choices in the list even when the
 *  search did not offer them as options, so the AI comparison covers them
 *  under a diagram-only id (never toggled as an option — only for hints). */
const DIAGRAM_HINT_PREFIX = 'diagram:';
const isKitRow = (p: DiagramPart): boolean =>
  isCatalogKit(p as any) || /^(yes|true|1)$/i.test(String((p as any)?.extra_details?.part_of_building_kit ?? ''));
const hintIdOf = (raw: RowRaw | undefined, prior?: boolean): string | undefined => {
  if (!raw || prior) return undefined;
  if (raw.optionId) return raw.optionId;
  const pid = String(raw.part?.partId ?? raw.part?.part_number ?? '').trim();
  // Kits, and parts the scorer rated above the weakest candidate (the list
  // lifts those into the top section), are compared by the hint too.
  return pid && (isKitRow(raw.part) || raw.promoted) ? `${DIAGRAM_HINT_PREFIX}${pid}` : undefined;
};

const PRIMARY = '#6866ff';
// Disabled tint of PRIMARY — same value the question form uses for its buttons.
const PRIMARY_DISABLED = '#c5c4ff';
// The app's settled green (cart / answered).
const ANSWERED_GREEN = '#137F67';

// Internal part-record field names that must never leak into a displayed name.
const PART_META_FIELDS = 'parentPartId|secondaryPartId|partItemId|parentPartItemId|calloutLabel|paddedCallout|secCrossCatKey|alphaSortSequence|alphaSort|addedManually|filtered|formattedPartNumber|manufacturer|partType|remarks';
const PART_META_BLOB = new RegExp(`\\s*\\((?:${PART_META_FIELDS})\\s*:`, 'i');

/**
 * Strip a trailing serialized-part metadata blob that occasionally leaks into an
 * option label / catalog name, e.g.
 *   "CUBIERTA PARAGOLPES TRAS INF (parentPartId: 627079 | calloutLabel: 86611F | …)"
 * → "CUBIERTA PARAGOLPES TRAS INF". Ordinary parentheticals like "(L)" stay.
 */
const cleanPartName = (name: string | null | undefined): string => {
  if (!name) return '';
  const m = name.search(PART_META_BLOB);
  return (m > 0 ? name.slice(0, m) : name).trim();
};

/** Normalized part identity for option↔diagram-part matching — alnum-only,
 * uppercased, so formatted numbers ("10266-527") match their bare form. */
const normPartKey = (v: unknown): string =>
  String(v ?? '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();

/**
 * Tag weight for one clarification row (diagram tag AND its mirrored list chip).
 *
 * Every candidate for this question is drawn IDENTICALLY — indigo outline, no
 * ★, no rank. A FILLED navy tag means the USER picked it, never that we did:
 * this question exists precisely because the search could NOT tell the
 * candidates apart, so ranking them would display a confidence we don't have.
 */
const candidatePriority = (
  r: { isOption: boolean; partName?: string | null; isSelected?: boolean; isFocused?: boolean },
): CalloutPriority =>
  r.isSelected && r.isOption ? 'hero'
    : r.isOption && r.isFocused !== false ? 'secondary'
      : r.partName ? 'optional' : 'na';

/** Dynamic focus: when the scorer separated a clear top tier, candidates that
 *  fall more than this far below the best estimate leave the candidate block
 *  and join "Other parts in this diagram" (still selectable) so the user
 *  compares three parts, not four. */
const FOCUS_BAND = 0.10;
const demotedCandidateIds = (options: ClarificationQuestion['options'] | undefined): Set<string> => {
  const scored = (options || []).filter(o =>
    typeof o.probability_score === 'number' && !o.extra_details?._expert_action && !o.extra_details?._expert_candidates);
  if (scored.length < 3) return new Set();
  const top = Math.max(...scored.map(o => o.probability_score as number));
  const floor = top - FOCUS_BAND;
  const focused = scored.filter(o => (o.probability_score as number) >= floor);
  // A single "winner" would not be a question; never narrow below two.
  if (focused.length < 2 || focused.length === scored.length) return new Set();
  return new Set(scored.filter(o => (o.probability_score as number) < floor).map(o => o.id));
};

const ClarificationDiagramInline: React.FC<Props> = ({
  question, t, selectedOptionIds, onToggleOption, onSelectOther, onClearOther,
  otherSelected, otherText, otherKey, disabled,
  pickedPartIds, priorPicks, onPickPart, onUnpickPart,
  noneSelected, noneLabel,
  onConfirm, confirmDisabled, onClearSelection, onCantFind, headerLabel,
  onRequestHint,
  openFullscreenOnMount, onFullscreenChange, openRequest, onOpeningChange, requestLine, answered, stepper, confirmLabel, displayQuestion,
}) => {
  const model3d = useOrgFeatureFlag('diagram_3d_models');
  // Falls back to the guest/portal session id registered on ChatService — those
  // pages have no SessionProvider, so the context accessor returns '' there and
  // the diagram would otherwise never load in guest mode.
  const sessionId = useActiveSessionIdSafe() || chatService.getGuestSessionId();
  const { i18n } = useTranslation();
  const { getActiveOrderId } = useOrder();
  const [diagrams, setDiagrams] = useState<Record<string, SectionDiagram | null>>({});
  const [loading, setLoading] = useState(true);
  // Catalog section names arrive in English; translated for the area tabs.
  const [sectionNames, setSectionNames] = useState<Record<string, string>>({});
  const demotedOptionIds = useMemo(() => demotedCandidateIds(question.options), [question.options]);

  const tr = (key: string, def: string) => (t ? t(key, { defaultValue: def }) : def);
  // i18next-shaped variant for shared components that pass `{ defaultValue }` themselves.
  // Without a translator the default still fills its {{placeholders}}.
  const tOpts = (key: string, opts?: any) => (t ? t(key, opts)
    : String(opts?.defaultValue ?? key).replace(/\{\{(\w+)\}\}/g, (_m: string, k: string) => String(opts?.[k] ?? '')));
  const selected = useMemo(() => new Set(selectedOptionIds), [selectedOptionIds]);
  const selectedSummary = useMemo(() => {
    if (noneSelected) return noneLabel || tr('chat.clarification.partNotInList', "I can't find the part in the list");
    const values = (question.options || [])
      .filter(option => selected.has(option.id))
      .map(option => {
        const ref = option.load?.[0];
        const diagram = ref ? diagrams[`${ref.group_id}/${ref.section_id}`] : null;
        const scopedParts = ref?.callout != null
          ? diagram?.parts.filter(part => String(part.callout) === String(ref.callout)) || []
          : diagram?.parts || [];
        const refPartId = normPartKey(ref?.partId);
        const refPartNumber = normPartKey(ref?.part_number);
        const identityHit = (refPartId && scopedParts.find(part => normPartKey(part.partId) === refPartId))
          || (refPartNumber && scopedParts.find(part => normPartKey(part.part_number) === refPartNumber));
        const calloutHits = !identityHit && ref?.callout != null
          ? diagram?.parts.filter(part => String(part.callout) === String(ref.callout)) || []
          : [];
        const hit = identityHit || (calloutHits.length === 1 ? calloutHits[0] : null);
        const partNumber = hit?.part_number || ref?.part_number || ref?.partId;
        return [ref?.callout ? String(ref.callout) : '', cleanPartName(hit?.partName || option.label), partNumber ? `#${displayPartRef(partNumber)}` : ''].filter(Boolean).join(' · ');
      });
    for (const partId of pickedPartIds || []) {
      const hit = Object.values(diagrams).flatMap(d => d?.parts || []).find(p => String(p.partId) === String(partId));
      values.push(hit ? [hit.callout ? String(hit.callout) : '', cleanPartName(hit.partName), hit.part_number ? `#${displayPartRef(hit.part_number)}` : ''].filter(Boolean).join(' · ') : String(partId));
    }
    if (otherSelected && otherText) values.push(otherText);
    return values.filter(Boolean).join(', ');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noneSelected, noneLabel, question.options, selected, pickedPartIds, diagrams, otherSelected, otherText]);
  const pickedSet = useMemo(() => new Set((pickedPartIds ?? []).map(String)), [pickedPartIds]);
  const otherPick = (otherSelected && (otherText ?? '').trim()) ? (otherText ?? '').trim() : null;
  // Every part the user has picked for this question, on any diagram: listed
  // options, parts clicked straight off a drawing, a part chosen by name.
  const optionIdSet = useMemo(() => new Set((question.options || []).map(o => o.id)), [question.options]);
  const pickedOptionIds = (selectedOptionIds || []).filter(id => optionIdSet.has(id));
  const selectedCount = noneSelected ? 0 : pickedOptionIds.length + (pickedPartIds?.length ?? 0) + (otherPick ? 1 : 0);
  const clearSelection = () => {
    if (disabled) return;
    if (onClearSelection) { onClearSelection(); return; }
    for (const id of pickedOptionIds) onToggleOption(id);
    for (const pid of pickedPartIds || []) onUnpickPart(pid);
    if (otherPick) onClearOther();
  };

  // Per-section identity sets of the parts chosen in OTHER questions of this
  // form. Matched by part identity (partId / part_number, normalized) when the
  // ref carries one; a ref with NO id falls back to its callout.
  const priorBySection = useMemo(() => {
    const out = new Map<string, { ids: Set<string>; callouts: Set<string> }>();
    for (const ref of priorPicks ?? []) {
      if (!ref.group_id || !ref.section_id) continue;
      const key = `${ref.group_id}/${ref.section_id}`;
      const entry = out.get(key) ?? { ids: new Set<string>(), callouts: new Set<string>() };
      const pid = normPartKey(ref.partId);
      const pnum = normPartKey(ref.part_number);
      if (pid) entry.ids.add(pid);
      if (pnum) entry.ids.add(pnum);
      if (!pid && !pnum && ref.callout != null && String(ref.callout)) entry.callouts.add(String(ref.callout));
      out.set(key, entry);
    }
    return out;
  }, [priorPicks]);
  const isPriorPart = (key: string, p: DiagramPart): boolean => {
    const prior = priorBySection.get(key);
    if (!prior) return false;
    const pid = normPartKey(p.partId);
    const pnum = normPartKey(p.part_number);
    if (pid && prior.ids.has(pid)) return true;
    if (pnum && prior.ids.has(pnum)) return true;
    return p.callout != null && prior.callouts.has(String(p.callout));
  };

  // Unique catalog sections referenced by this question's options. Each carries
  // the best-scored option's partId so a multi-page section draws THAT part's
  // page (Kia fuel filter sits on page 2 of 7; page 1 was the wrong drawing).
  const sections: SectionRef[] = useMemo(() => {
    const map = new Map<string, SectionRef & { score: number }>();
    for (const opt of question.options || []) {
      const score = typeof opt.probability_score === 'number' ? opt.probability_score : -1;
      for (const ref of (opt.load || [])) {
        if (!ref.group_id || !ref.section_id) continue;
        const key = `${ref.group_id}/${ref.section_id}`;
        const prev = map.get(key);
        if (prev && prev.score >= score) continue;
        map.set(key, { group_id: ref.group_id, section_id: ref.section_id, part_id: ref.partId ? String(ref.partId) : undefined, score });
      }
    }
    return Array.from(map.values(), ({ score: _s, ...ref }) => ref);
  }, [question]);
  const keyOf = (s: SectionRef) => `${s.group_id}/${s.section_id}`;

  // Stable identity of the sections set — the parent may hand us a new `question`
  // object on every render; depend the fetch on this primitive instead.
  const sectionsKey = useMemo(() => sections.map((s) => `${keyOf(s)}/${s.part_id || ''}`).join(','), [sections]);

  // Simple, human tab label per section: the front/rear + left/right classes of
  // the option(s) whose load refs point to it. Fail-open: a section whose
  // options span both values of a dimension gets no word for it; a label another
  // section duplicates is dropped (it wouldn't distinguish the tabs).
  const sectionSimpleLabels = useMemo(() => {
    const classes = new Map<string, { front: boolean; rear: boolean; left: boolean; right: boolean }>();
    for (const opt of question.options || []) {
      const fromChips = positionClassesFromName(positionChipsFromExtra(opt.extra_details).join(' '));
      const fromLabel = positionClassesFromName(cleanPartName(opt.label));
      const position = fromChips.position ?? fromLabel.position;
      const side = fromChips.side ?? fromLabel.side;
      if (!position && !side) continue;
      for (const ref of (opt.load || [])) {
        if (!ref.group_id || !ref.section_id) continue;
        const key = `${ref.group_id}/${ref.section_id}`;
        const c = classes.get(key) ?? { front: false, rear: false, left: false, right: false };
        if (position) c[position] = true;
        if (side) c[side] = true;
        classes.set(key, c);
      }
    }
    const labels = new Map<string, string>();
    classes.forEach((c, key) => {
      const words: string[] = [];
      if (c.front !== c.rear) words.push(c.front ? tr('chat.clarification.posFront', 'Front') : tr('chat.clarification.posRear', 'Rear'));
      if (c.left !== c.right) words.push(c.left ? tr('chat.clarification.posLeft', 'Left') : tr('chat.clarification.posRight', 'Right'));
      if (words.length) labels.set(key, words.join(' '));
    });
    const counts = new Map<string, number>();
    labels.forEach(v => counts.set(v, (counts.get(v) ?? 0) + 1));
    const dupKeys: string[] = [];
    labels.forEach((v, k) => { if ((counts.get(v) ?? 0) > 1) dupKeys.push(k); });
    dupKeys.forEach(k => labels.delete(k));
    return labels;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question, t, i18n.language]);

  // Tab text: the catalog's own section name (translated); else the derived
  // position/side label; the caller falls back to "Diagram N".
  const sectionTabLabel = (key: string): string | null => {
    const name = (diagrams[key]?.section_name || '').trim();
    if (name) return sectionNames[name] || name;
    return sectionSimpleLabels.get(key) || null;
  };

  // Scores for parts the scorer ranked BELOW the ask threshold (never options),
  // so "other parts in this diagram" rows can show their estimate badge.
  const { scoredByPart, scoredBySectionCallout } = useMemo(() => {
    const scoredByPart = new Map<string, number>();
    const scoredBySectionCallout = new Map<string, number>();
    for (const sp of question.scored_parts || []) {
      if (!sp || typeof sp.probability_score !== 'number' || sp.probability_score <= 0) continue;
      if (sp.partId) scoredByPart.set(normPartKey(sp.partId), sp.probability_score);
      if (sp.part_number) scoredByPart.set(normPartKey(sp.part_number), sp.probability_score);
      if (sp.group_id && sp.section_id && sp.callout) {
        scoredBySectionCallout.set(`${sp.group_id}/${sp.section_id}/${String(sp.callout)}`, sp.probability_score);
      }
    }
    return { scoredByPart, scoredBySectionCallout };
  }, [question.scored_parts]);

  // partId / (section,callout) → option id, plus per-option metadata. When an
  // option's load-ref ids are locatable in a diagram, callout matching alone
  // must NOT claim a diagram part (left + right headlamps are all "1").
  const { byPartId, byPartNumber, fallbackPartId, fallbackPartNumber, bySectionCallout, optionMeta, optionRefIds } = useMemo(() => {
    const byPartId = new Map<string, string>();
    const byPartNumber = new Map<string, string>();
    const fallbackPartId = new Map<string, string>();
    const fallbackPartNumber = new Map<string, string>();
    const ambiguousPartIds = new Set<string>();
    const ambiguousPartNumbers = new Set<string>();
    const ambiguousFallbackPartIds = new Set<string>();
    const ambiguousFallbackPartNumbers = new Set<string>();
    const bySectionCallout = new Map<string, string>();
    const optionRefIds = new Map<string, { partIds: string[]; partNumbers: string[] }>();
    const optionMeta = new Map<string, { label: string; description?: string | null; score: number | null; extra_details?: Record<string, string> | null }>();
    for (const opt of question.options || []) {
      optionMeta.set(opt.id, {
        label: opt.label,
        description: opt.description,
        score: typeof opt.probability_score === 'number' ? opt.probability_score : null,
        extra_details: opt.extra_details ?? null,
      });
      for (const ref of (opt.load || [])) {
        const scope = `${ref.group_id}/${ref.section_id}/${ref.callout != null ? String(ref.callout) : ''}`;
        const addIdentity = (map: Map<string, string>, ambiguous: Set<string>, value: unknown, keyScope = scope) => {
          const identity = normPartKey(value);
          const key = `${keyScope}/${identity}`;
          if (!identity || ambiguous.has(key)) return;
          const previous = map.get(key);
          if (previous && previous !== opt.id) {
            map.delete(key);
            ambiguous.add(key);
            return;
          }
          map.set(key, opt.id);
        };
        const refs = optionRefIds.get(opt.id) ?? { partIds: [], partNumbers: [] };
        if (ref.partId) {
          addIdentity(byPartId, ambiguousPartIds, ref.partId);
          addIdentity(fallbackPartId, ambiguousFallbackPartIds, ref.partId, `${ref.group_id}/${ref.section_id}`);
          refs.partIds.push(normPartKey(ref.partId));
        }
        if (ref.part_number) {
          addIdentity(byPartNumber, ambiguousPartNumbers, ref.part_number);
          addIdentity(fallbackPartNumber, ambiguousFallbackPartNumbers, ref.part_number, `${ref.group_id}/${ref.section_id}`);
          refs.partNumbers.push(normPartKey(ref.part_number));
        }
        optionRefIds.set(opt.id, refs);
        if (ref.callout) bySectionCallout.set(`${ref.group_id}/${ref.section_id}/${String(ref.callout)}`, opt.id);
      }
    }
    return { byPartId, byPartNumber, fallbackPartId, fallbackPartNumber, bySectionCallout, optionMeta, optionRefIds };
  }, [question]);

  // Per-section set of the diagram parts' own identities — tells `resolve`
  // whether an option's load-ref ids live in the SAME id-space as this
  // diagram's parts (if not, callout matching stays authoritative).
  const diagramIdsByKey = useMemo(() => {
    const out = new Map<string, { partIds: Set<string>; partNumbers: Set<string> }>();
    for (const [key, d] of Object.entries(diagrams)) {
      const partIds = new Set<string>();
      const partNumbers = new Set<string>();
      for (const p of d?.parts || []) {
        const pid = normPartKey(p.partId);
        const pnum = normPartKey(p.part_number);
        if (pid) partIds.add(pid);
        if (pnum) partNumbers.add(pnum);
      }
      out.set(key, { partIds, partNumbers });
    }
    return out;
  }, [diagrams]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!sessionId || sections.length === 0) { setLoading(false); return; }
      setLoading(true);
      const out: Record<string, SectionDiagram | null> = {};
      await Promise.all(sections.map(async (s) => {
        const key = keyOf(s);
        try {
          const diagram = await chatService.getSectionDiagram(sessionId, s.group_id, s.section_id, s.part_id);
          out[key] = { ...diagram, parts: collapseSupersededPartRows(diagram.parts || []) };
        }
        catch { out[key] = null; }
      }));
      if (!cancelled) { setDiagrams(out); setLoading(false); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, sectionsKey]);

  useEffect(() => { setSectionNames({}); }, [i18n.language]);
  useEffect(() => {
    const texts = Array.from(new Set(Object.values(diagrams).map(d => (d?.section_name || '').trim()).filter(Boolean)));
    if (!texts.length) return;
    const items = texts.map((text, i) => ({ key: String(i), text }));
    let cancelled = false;
    TranslationBatchManager.request(String(getActiveOrderId() ?? ''), i18n.language || '', items)
      .then(res => {
        if (cancelled) return;
        const map: Record<string, string> = {};
        items.forEach(it => { const v = res[it.key]; if (v) map[it.text] = v; });
        if (Object.keys(map).length) setSectionNames(prev => ({ ...prev, ...map }));
      })
      .catch(() => { /* leave originals on failure */ });
    return () => { cancelled = true; };
  }, [diagrams, i18n.language, getActiveOrderId]);

  // Resolve one catalog part on a section to its choice and score.
  const resolve = (key: string, part: DiagramPart) => {
    const [g, s] = key.split('/');
    const label = String(part.callout ?? '');
    // Part IDENTITY first: several different parts share one callout (left +
    // right headlamps are all "1"), so a callout match alone must not claim a
    // row the option's load refs can positively identify as a different part.
    const pid = normPartKey(part.partId);
    const pnum = normPartKey(part.part_number);
    const calloutScope = `${g}/${s}/${label}`;
    const sectionScope = `${g}/${s}/`;
    let optionId = (pid && byPartId.get(`${calloutScope}/${pid}`))
      || (pnum && byPartNumber.get(`${calloutScope}/${pnum}`))
      || (pid && byPartId.get(`${sectionScope}/${pid}`))
      || undefined;
    if (!optionId && pnum) {
      const viaPartNumber = byPartNumber.get(`${sectionScope}/${pnum}`);
      if (viaPartNumber) {
        const dIds = diagramIdsByKey.get(key);
        const exactRefIds = (optionRefIds.get(viaPartNumber)?.partIds || []).filter(id => dIds?.partIds.has(id));
        // A manufacturer number can repeat across supersession rows and even
        // different callouts. Once this option's internal id exists on the
        // drawing, that exact id owns the match; the number is only a fallback.
        if (!exactRefIds.length || (!!pid && exactRefIds.includes(pid))) optionId = viaPartNumber;
      }
    }
    if (!optionId) {
      const fallback = (pid && fallbackPartId.get(`${g}/${s}/${pid}`))
        || (pnum && fallbackPartNumber.get(`${g}/${s}/${pnum}`))
        || undefined;
      if (fallback) {
        const diagramCallouts = new Set((diagrams[key]?.parts || []).map(p => String(p.callout ?? '')));
        const refs = (question.options || []).find(opt => opt.id === fallback)?.load || [];
        const scopedCallouts = refs
          .filter(ref => String(ref.group_id) === g && String(ref.section_id) === s && ref.callout != null)
          .map(ref => String(ref.callout));
        // Some older question payloads carry a stale callout but a valid
        // internal part id. Trust that id only when the stale callout does not
        // exist in this diagram. If it does exist, keep identity inside it.
        if (!scopedCallouts.some(callout => diagramCallouts.has(callout))) optionId = fallback;
      }
    }
    if (!optionId) {
      const viaCallout = bySectionCallout.get(`${g}/${s}/${label}`);
      // Callout fallback only when identity CAN'T decide. Identity IS decidable
      // when this row has an id AND the option's ref ids resolve to real parts
      // in this diagram (same id-space) — then a non-matching row is a
      // different part that merely shares the callout.
      if (viaCallout) {
        const dIds = diagramIdsByKey.get(key);
        const refs = optionRefIds.get(viaCallout);
        const identityDecidable = !!(
          (pid && refs?.partIds.some(id => dIds?.partIds.has(id)))
          || (pnum && refs?.partNumbers.some(id => dIds?.partNumbers.has(id)))
        );
        if (!identityDecidable) optionId = viaCallout;
      }
    }
    const meta = optionId ? optionMeta.get(optionId) : undefined;
    // The catalog's own name first: an option label is our reading of the
    // request ("Panel assembly, front", or a reworded "Left") — the side and
    // the difference are the AI hint's job on this row.
    const partName = cleanPartName(part.partName || (optionId && meta?.label) || null) || null;
    // Stable identity for this row: section + callout + internal partId. Pins a
    // free-text pick to the EXACT clicked part (several rows can share a name).
    const rowKey = `${key}#${label}#${part.partId ?? ''}`;
    const isPicked = !!(part.partId && pickedSet.has(String(part.partId)));
    const isSelected = optionId
      ? selected.has(optionId) || isPicked
      : isPicked || !!(otherPick && (otherKey ? otherKey === rowKey : partName && otherPick === partName.trim()));
    // Chosen in a PREVIOUS question of this form that shares this diagram.
    // This question's own selection wins.
    const isPrior = !isSelected && isPriorPart(key, part);
    return {
      partName,
      rowKey,
      description: meta?.description ?? null,
      extra_details: meta?.extra_details ?? null,
      // The option's own score for a candidate; for any other catalog part,
      // the below-threshold score the scorer gave it (if it ranked it at all).
      score: meta?.score
        ?? (pid ? scoredByPart.get(pid) : undefined)
        ?? (pnum ? scoredByPart.get(pnum) : undefined)
        ?? scoredBySectionCallout.get(`${g}/${s}/${label}`)
        ?? null,
      isOption: !!optionId,
      // False for a candidate the dynamic focus moved under "Other parts".
      isFocused: !optionId || !demotedOptionIds.has(optionId),
      optionId,
      isSelected,
      isPrior,
    };
  };

  // Rows and the hint-scoped question are rebuilt from `resolve` for every
  // render; cache per section key for as long as their inputs stand.
  const rowsCache = useMemo(
    () => new Map<string, { d: SectionDiagram; rows: DiagramRow[]; scoped?: ClarificationQuestion }>(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [diagrams, question, selected, pickedSet, demotedOptionIds, priorBySection, byPartId, byPartNumber,
      fallbackPartId, fallbackPartNumber, bySectionCallout,
      optionMeta, optionRefIds, diagramIdsByKey, scoredByPart, scoredBySectionCallout, otherPick, otherKey],
  );
  const rowsFor = (key: string, d: SectionDiagram): DiagramRow[] => {
    const hit = rowsCache.get(key);
    if (hit && hit.d === d) return hit.rows;
    const resolved = (d.parts || []).map(p => ({ p, r: resolve(key, p) }));
    // An option that matched SEVERAL distinct parts (left vs right fender —
    // different part numbers) can't be toggled as one: those rows select by
    // their OWN partId, independently.
    const partsPerOption = new Map<string, Set<string>>();
    for (const { p, r } of resolved) {
      if (!r.optionId || r.isPrior) continue;
      const pk = normPartKey(p.part_number || p.partId);
      if (!pk) continue;
      const set = partsPerOption.get(r.optionId) ?? new Set<string>();
      set.add(pk);
      partsPerOption.set(r.optionId, set);
    }
    // Mirrors DiagramModal's promotion: a non-option scored above the weakest
    // focused option is listed with the candidates, so the hint covers it.
    const optionScores = resolved.filter(({ r }) => r.optionId && r.isFocused && !r.isPrior && typeof r.score === 'number').map(({ r }) => r.score as number);
    const promoteFloor = optionScores.length ? Math.min(...optionScores) : null;
    const rows = resolved.map(({ p, r }): DiagramRow => {
      const pid = p.partId ? String(p.partId) : '';
      const ambiguous = !!r.optionId && (partsPerOption.get(r.optionId)?.size ?? 0) > 1 && !!pid;
      const isSelected = ambiguous ? pickedSet.has(pid) : r.isSelected;
      const name = cleanPartName(p.partName) || r.partName;
      const pk = normPartKey(p.part_number || pid);
      const promoted = !r.optionId && promoteFloor != null && typeof r.score === 'number' && r.score > promoteFloor;
      const raw: RowRaw = { part: p, optionId: r.optionId, ambiguous, promoted };
      return {
        key: r.rowKey,
        callout: String(p.callout ?? ''),
        partId: pid || null,
        partNumber: p.part_number ?? null,
        name,
        description: r.description,
        extra_details: (p.extra_details && Object.keys(p.extra_details).length) ? p.extra_details : r.extra_details,
        axes: p.axes ?? null,
        score: r.score,
        candidate: r.isOption && r.isFocused,
        priority: candidatePriority({ isOption: r.isOption, partName: name, isSelected, isFocused: r.isFocused }),
        selected: isSelected,
        prior: r.isPrior,
        // A bundle is the SAME part (number) under one option at several
        // callouts; a plain part is one row per catalog identity.
        bundleKey: r.optionId ? (pk ? `${r.optionId}::${pk}` : r.rowKey) : pid ? `id:${pid}` : (pk || `#${p.callout ?? ''}`),
        raw,
      };
    });
    rowsCache.set(key, { d, rows });
    return rows;
  };

  // Click on a row / tag: a suggested option toggles its option id; any other
  // catalog part with a real partId is a CONCRETE pick (loaded exactly on the
  // answer turn — the fast-path); a part with no partId falls back to the
  // free-text pick.
  const toggleRow = (key: string) => (row: DiagramRow, next: boolean) => {
    if (disabled) return;
    const { part, optionId, ambiguous } = row.raw as RowRaw;
    const [g, s] = key.split('/');
    const pid = part.partId ? String(part.partId) : '';
    if (optionId && !ambiguous) { onToggleOption(optionId); return; }
    if (pid) {
      if (ambiguous && optionId && selected.has(optionId)) onToggleOption(optionId); // drop the shared option
      if (pickedSet.has(pid)) onUnpickPart(pid);
      else onPickPart({ group_id: g, section_id: s, partId: pid, part_name: row.name, callout: row.callout });
      return;
    }
    if (row.name) (next ? onSelectOther(row.name, row.key) : onClearOther());
  };

  // Fullscreen modal: `${group_id}/${section_id}` of the open section, or null.
  const [modalKey, setModalKey] = useState<string | null>(null);
  // Other sections that may hold the part, keyed by the section being viewed.
  // Loaded only when the diagram's "other sections" menu is opened — null means
  // "not asked yet", [] is a real answer ("this part lives in one section").
  const [candidates, setCandidates] = useState<Record<string, CandidateSection[]>>({});
  const loadCandidates = (key: string, groupId: string, sectionId: string) => {
    if (candidates[key]) return;
    const query = [question.part_label, question.part_name].filter(Boolean).join(' ').trim();
    if (!query) { setCandidates(prev => ({ ...prev, [key]: [] })); return; }
    // Asked in the background when the viewer opens: a failure (even a thrown
    // one) only means no other categories to offer, never a broken viewer.
    Promise.resolve()
      .then(() => chatService.getCandidateSections(sessionId, query, groupId, sectionId))
      .then(res => setCandidates(prev => ({ ...prev, [key]: res?.sections || [] })))
      .catch(() => setCandidates(prev => ({ ...prev, [key]: [] })));
  };
  const openModal = (key: string) => { setModalKey(key); onFullscreenChange?.(true); };
  // Another category the resolver says may hold the part: load its diagram
  // (its parts are picked like any diagram part) and open it.
  const openCandidate = async (c: CandidateSection) => {
    const key = `${c.group_id}/${c.section_id}`;
    if (!diagrams[key]) {
      try {
        const d = await chatService.getSectionDiagram(sessionId, c.group_id, c.section_id);
        setDiagrams(prev => ({ ...prev, [key]: { ...d, parts: collapseSupersededPartRows(d.parts || []) } }));
      } catch { return; }
    }
    setActiveSection(key);
    setModalKey(key);
  };
  const closeModal = () => { setModalKey(null); onFullscreenChange?.(false); };
  // When ONE question references several section diagrams we show one at a
  // time; `${group_id}/${section_id}` of the visible one (null → the first).
  const [activeSection, setActiveSection] = useState<string | null>(null);
  // Continue the fullscreen flow: this question mounted because the user
  // confirmed the previous one inside the map. A multi-area question returns
  // to its outside cards first so the user chooses the area deliberately.
  useEffect(() => {
    if (openFullscreenOnMount && sections.length === 1) setModalKey(keyOf(sections[0]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // A chip click on the card (or "Open the EPC diagram") opens the map on the
  // clicked option's section — once its drawing has loaded, so the viewer
  // opens complete instead of as an empty shell; until then the caller shows
  // a spinner (onOpeningChange).
  const handledOpen = React.useRef<number | null>(null);
  useEffect(() => {
    if (!openRequest || sections.length === 0 || handledOpen.current === openRequest.nonce) return;
    const ref = (question.options || []).find(o => o.id === openRequest.optionId)?.load?.[0];
    const want = ref?.group_id && ref?.section_id ? `${ref.group_id}/${ref.section_id}` : null;
    const key = want && sections.some(x => keyOf(x) === want) ? want : keyOf(sections[0]);
    if (loading) { onOpeningChange?.(true); return; }
    handledOpen.current = openRequest.nonce;
    const d = diagrams[key] as any;
    const url: string | null = d?.diagram_url || d?.sheets?.[0]?.diagram_url || null;
    const open = () => { onOpeningChange?.(false); setActiveSection(key); openModal(key); };
    // Inline (data:) images are already here; so is anything the browser has.
    if (!url || url.startsWith('data:') || typeof Image === 'undefined') { open(); return; }
    onOpeningChange?.(true);
    const img = new Image();
    let done = false;
    const finish = () => { if (!done) { done = true; open(); } };
    img.onload = finish;
    img.onerror = finish;
    // Never keep the customer waiting on a slow image: open after 8s anyway.
    const timer = window.setTimeout(finish, 8000);
    img.src = url;
    return () => { window.clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openRequest, sections.length, loading]);

  // The section on screen: the open map wins, else the selected tab, else the
  // first. Empty only when the question references no section at all.
  const activeKey = (activeSection && sections.some(x => keyOf(x) === activeSection))
    ? activeSection
    : (sections[0] ? keyOf(sections[0]) : '');

  // AI hint per section (`${group}/${section}`): fetched AUTOMATICALLY as soon
  // as the section's map has loaded, kept for the life of the component. Its
  // per-candidate tags land on the list rows; the pill opens the full
  // comparison, and retries after a failure.
  const [hints, setHints] = useState<Record<string, ClarificationHintState>>({});
  const [hintOpenKey, setHintOpenKey] = useState<string | null>(null);
  // The question block's answer options start OPEN: they are the question.
  const [answersOpen, setAnswersOpen] = useState(true);
  // An answer picked whose parts are on another diagram: switch, then filter there.
  const [answerRequest, setAnswerRequest] = useState<{ n: number; label: string; sectionKey: string; nonce: number } | null>(null);
  const AREAS_KEY = '__areas__';
  const areaState = hints[AREAS_KEY];
  const areaHint = areaState && areaState.status === 'ready' ? areaState.hint : null;
  // The modal repeats the exact area name shown on the outside card.
  const areaLabel = (key: string): string | null => areaHint?.area_labels?.[key] || sectionTabLabel(key);
  const hintRequested = React.useRef<Set<string>>(new Set());
  const hintPollAlive = React.useRef(true);
  // Set on EVERY mount, not just the first: StrictMode runs mount →
  // cleanup → mount in dev, and an effect that only returns a cleanup
  // leaves the ref false for the rest of the component's life, so every
  // `if (!hintPollAlive.current) return;` below silently drops its result.
  useEffect(() => {
    hintPollAlive.current = true;
    return () => { hintPollAlive.current = false; };
  }, []);
  const requestSectionHint = async (key: string, scoped: ClarificationQuestion, open = true) => {
    if (!onRequestHint) return;
    if (open) setHintOpenKey(key);
    const current = hints[key];
    if (current?.status === 'loading' || current?.status === 'ready') return;
    // A failed request may be retried from the pill; the auto-run never loops.
    if (hintRequested.current.has(key) && (current?.status !== 'error' || !open)) return;
    hintRequested.current.add(key);
    setHints(prev => ({ ...prev, [key]: { status: 'loading' } }));
    try {
      const hint = await onRequestHint(scoped);
      setHints(prev => ({ ...prev, [key]: hint?.generated ? { status: 'ready', hint } : { status: 'error' } }));
      // Fast catalog-only answer is on the rows; swap in the grounded one later.
      if (hint?.generated && hint.refining) {
        void pollRefinedHint(
          () => onRequestHint(scoped),
          refined => setHints(prev => ({ ...prev, [key]: { status: 'ready', hint: refined } })),
          () => hintPollAlive.current,
        );
      }
    } catch {
      setHints(prev => ({ ...prev, [key]: { status: 'error' } }));
    }
  };

  const partQuestionFor = (key: string): string => {
    const area = areaLabel(key) || key;
    return t
      ? t('chat.clarification.whichPartInArea', { defaultValue: 'Which part do you need in {{area}}?', area })
      : `Which part do you need in ${area}?`;
  };

  // The question scoped to ONE diagram for the AI hint: only the parts in the
  // area the user opened, each stamped with that diagram's OE number and
  // callout. The original front/rear question has already been answered by
  // opening the area card, so the hint now compares parts, not areas.
  const scopedQuestionFor = (key: string, d: SectionDiagram): ClarificationQuestion => {
    const rows = rowsFor(key, d);
    const hit = rowsCache.get(key)!;
    if (hit.scoped) return sections.length > 1
      ? { ...hit.scoped, text: partQuestionFor(key), missing: [] }
      : hit.scoped;
    const [gKey, sKey] = key.split('/');
    const seen = new Set<string>();
    const options = rows.flatMap(r => {
      const raw = r.raw as RowRaw;
      const { part: p, optionId } = raw;
      if (!optionId) {
        // A kit / kit component the search did not offer: compared too.
        const hid = hintIdOf(raw, r.prior);
        if (!hid || seen.has(hid)) return [];
        seen.add(hid);
        return [{
          id: hid,
          label: p.partName || r.name || '',
          ...(raw.promoted && typeof r.score === 'number' ? { probability_score: r.score } : {}),
          extra_details: p.extra_details ?? null,
          load: [{ group_id: gKey, section_id: sKey, partId: String(p.partId ?? ''), callout: p.callout ?? null, part_name: p.partName ?? null, part_number: p.part_number ?? null }],
        } as ClarificationQuestion['options'][number]];
      }
      if (r.prior || seen.has(optionId)) return [];
      const opt = question.options.find(o => o.id === optionId);
      if (!opt) return [];
      seen.add(optionId);
      const base = opt.load?.[0];
      const load: PartRefLite = {
        group_id: gKey,
        section_id: sKey,
        partId: String(p.partId ?? base?.partId ?? ''),
        callout: p.callout ?? base?.callout ?? null,
        part_name: base?.part_name ?? null,
        part_number: p.part_number ?? base?.part_number ?? null,
      };
      return [{ ...opt, label: p.partName || opt.label, extra_details: (p.extra_details ?? opt.extra_details) ?? null, load: [load] }];
    });
    // The comparison reads a capped number of rows: best estimate first, so a
    // low-match row late in catalog order never pushes out a likely answer.
    // Diagram-only kit rows go after the offered options.
    const rank = (o: { id: string; probability_score?: number | null }) =>
      typeof o.probability_score === 'number' ? o.probability_score : o.id.startsWith(DIAGRAM_HINT_PREFIX) ? -2 : -1;
    const ordered = options.map((o, i) => ({ o, i })).sort((a, b) => rank(b.o) - rank(a.o) || a.i - b.i).map(x => x.o);
    hit.scoped = {
      ...question,
      ...(sections.length > 1 ? { text: partQuestionFor(key), missing: [] } : {}),
      options: ordered,
    };
    return hit.scoped;
  };

  // The one comparison across EVERY diagram's candidates (each option named
  // with its area), or null while fewer than two are loaded.
  const areasQuestion = (): ClarificationQuestion | null => {
    if (sections.length < 2) return null;
    const seen = new Set<string>();
    const options = sections.flatMap(s => {
      const key = keyOf(s);
      const d = diagrams[key];
      if (!d) return [];
      return scopedQuestionFor(key, d).options
        .filter(o => !o.id.startsWith(DIAGRAM_HINT_PREFIX) && !seen.has(o.id) && seen.add(o.id))
        .map(o => ({ ...o, section_label: sectionTabLabel(key) || key }));
    });
    return options.length >= 2 ? { ...question, options } : null;
  };
  // What "What's different?" compares inside a diagram: its own candidates
  // when there are two or more to tell apart, else the candidates across all
  // the diagrams of the question (an air cleaner vs an oil filter, each alone
  // on its drawing). Null when there is nothing to compare at all.
  const hintScopeFor = (key: string, d: SectionDiagram): { key: string; question: ClarificationQuestion } | null => {
    const scoped = scopedQuestionFor(key, d);
    if (scoped.options.length >= 2) return { key, question: scoped };
    const all = areasQuestion();
    return all ? { key: AREAS_KEY, question: all } : null;
  };

  // Auto-run the part hint only after the user opens an area. The outside cards
  // own area selection; no part comparison should silently run for the first
  // area before the user has chosen one.
  useEffect(() => {
    if (loading || !onRequestHint || !modalKey) return;
    const d = diagrams[modalKey];
    if (!d) return;
    const scope = hintScopeFor(modalKey, d);
    if (!scope) return;
    void requestSectionHint(scope.key, scope.question, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diagrams, loading, !!onRequestHint, modalKey]);

  // Several diagrams: one comparison names the OUTSIDE area cards, and
  // stands in for "What's different?" on a diagram with a single candidate.
  useEffect(() => {
    if (loading || !onRequestHint) return;
    const all = areasQuestion();
    if (!all) return;
    void requestSectionHint(AREAS_KEY, all, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diagrams, loading, !!onRequestHint]);

  // "Show this candidate" from a reference in the comparison: light its row
  // the way a callout click does (scroll + timed navy ring).
  const [focusRequest, setFocusRequest] = useState<{ key: string; nonce: number } | null>(null);
  const focusSectionOption = (key: string, d: SectionDiagram, optionId: string) => {
    const row = rowsFor(key, d).find(r => hintIdOf(r.raw as RowRaw, r.prior) === optionId);
    if (row) { setFocusRequest(prev => ({ key: row.key, nonce: (prev?.nonce ?? 0) + 1 })); return; }
    // A candidate of another diagram (the comparison spans them all): switch
    // to its drawing, then light its row there.
    for (const sec of sections) {
      const k = keyOf(sec);
      const od = diagrams[k];
      if (k === key || !od) continue;
      const there = rowsFor(k, od).find(r => hintIdOf(r.raw as RowRaw, r.prior) === optionId);
      if (!there) continue;
      setActiveSection(k);
      setModalKey(k);
      setFocusRequest(prev => ({ key: there.key, nonce: (prev?.nonce ?? 0) + 1 }));
      return;
    }
  };

  // Prefetch the translation of the section on screen as soon as its drawing
  // is here, so "Open the EPC diagram" does not wait for it: the viewer
  // opens only once every catalog string is translated, and this makes that
  // a cache hit instead of a round trip on the click.
  const prefetched = React.useRef<Set<string>>(new Set());
  useEffect(() => {
    const pKey = activeKey;
    const pd = pKey ? diagrams[pKey] : null;
    if (!pd || loading || !pKey) return;
    const lang = i18n.language || '';
    const stamp = `${pKey}|${lang}`;
    if (prefetched.current.has(stamp)) return;
    prefetched.current.add(stamp);
    // The requested type too: its green tag then reads translated at once.
    const req = requestedTypeOf(question.part_label || question.part_name);
    const texts = Array.from(new Set([...diagramTexts(rowsFor(pKey, pd)), ...(req ? [req] : [])]));
    const items = texts.map((text, i) => ({ key: String(i), text }));
    if (!items.length) return;
    TranslationBatchManager.request(String(getActiveOrderId() ?? ''), lang, items).catch(() => { /* the viewer translates on open */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diagrams, loading, activeKey, i18n.language]);
  if (loading) {
    // Same `translate="no"` opt-out as the main return: browser auto-translate
    // rewriting this text node crashes React when the branch swaps.
    return (
      <div translate="no" className="notranslate" style={{ minHeight: 220, display: 'flex', alignItems: 'center', gap: 8, color: '#666', padding: 16, justifyContent: 'center', background: 'linear-gradient(110deg, #fafafa 8%, #f1f1f5 18%, #fafafa 33%)', backgroundSize: '200% 100%', border: `1px solid ${DIAGRAM_PALETTE.border}`, borderRadius: 10 }}>
        <Loader2 size={16} className="animate-spin" /> {tr('chat.clarification.diagramLoading', 'Preparing diagram…')}
      </div>
    );
  }
  if (sections.length === 0) return null;

  const multi = sections.length > 1;
  const s = sections.find(x => keyOf(x) === activeKey) || sections[0];
  const key = keyOf(s);
  const d = diagrams[key];
  const md = modalKey ? diagrams[modalKey] : null;
  const modalRows = modalKey && md ? rowsFor(modalKey, md) : [];

  // ── Tags fed to the parts list ─────────────────────────────────────────────
  // What was asked for (drives the green / red functionality tag), the
  // numbered answer each row satisfies, and the AI comparison's
  // distinguishing values (shown in the row's ONE tag row, not a second box).
  const requestedType = requestedTypeOf(question.part_label || question.part_name);
  const modalScope = modalKey && md ? hintScopeFor(modalKey, md) : null;
  const modalHintState = modalScope ? hints[modalScope.key] : undefined;
  const modalHint = modalHintState?.status === 'ready'
    ? (modalHintState as { status: 'ready'; hint: ClarificationHint }).hint : null;
  const modalScoped = modalScope && modalHint ? modalScope.question : null;
  const answerSet = modalHint && modalScoped
    ? answerOptionsFrom(modalHint.factors, modalScoped.options.map(o => o.id), modalHint.differentiator)
    : null;
  // The parts' answer tags follow the chat card's numbering: a narrowing
  // question's answers group parts, so each part wears its answer. When the
  // chat card's answers ARE the parts, a tag would only repeat the title. Only
  // without the chat card's question do the AI comparison's answers stand in.
  const answerOf = new Map<string, { n: number; label: string }>();
  if (displayQuestion) {
    // A catch-all ("Both") holds every part the others do: as a tag it would
    // overwrite each part's own answer, and as a chip it filters nothing.
    if (displayQuestion.tagged) for (const a of displayQuestion.answers) {
      if (isCatchAllAnswer(a, displayQuestion.answers)) continue;
      for (const id of a.optionIds) answerOf.set(id, { n: a.n, label: a.title });
    }
  } else {
    for (const a of answerSet?.answers || []) for (const id of a.optionIds) answerOf.set(id, { n: a.n, label: a.label });
  }
  const rowAnswer = (row: DiagramRow) => {
    const id = hintIdOf(row.raw as RowRaw, row.prior);
    return (id && answerOf.get(id)) || null;
  };
  const rowAiTags = (row: DiagramRow, { shownValues, names }: { shownValues: string[]; names: string[] }): AiRowTags | null => {
    const id = hintIdOf(row.raw as RowRaw, row.prior);
    if (!modalHint || !modalScoped || !id) return null;
    const candidate = resolveCandidate(modalHint, modalScoped, id);
    if (!candidate) return null;
    const option = modalScoped.options.find(o => o.id === id);
    // The factor the answers were built from is the answer tag already.
    return aiRowTagsOf(
      { part_name: question.part_label || question.part_name, part_parameters: [], part_refs: option?.load || [], candidate },
      tOpts, { exclude: shownValues, names }, answerSet?.title || '',
    );
  };

  // The question block — the live pattern of the chat's question card: the
  // status pill, the grey PART REQUEST bar with our normalised reading of the
  // request (not the customer's wording, and no match count: the count on top
  // of the parts list carries it), the question at 19px, then the answer
  // options as numbered rows (part count + what they cover), folded at first.
  const renderHeader = (ctx?: DiagramHeaderCtx) => {
    if (!modalKey) return null;
    const base = (requestLine?.label || headerLabel || question.part_label || question.part_name || '').trim();
    const label = refinedRequestLabel(base, question.narrowing_trail);
    // The chat card's question, word for word, when the card handed it over.
    const modalQuestion = displayQuestion?.text || (md ? scopedQuestionFor(modalKey, md).text : question.text);
    // One candidate still gets the hint: it says what to check on the car
    // before confirming ("What's different?" needs two to compare).
    // Each answer: how many parts satisfy it and what they are — the chat
    // card's own answers (title, count, grey line) when it handed them over.
    const answers = displayQuestion ? displayQuestion.answers.map(a => {
      const rows = modalRows.filter(r => { const id = hintIdOf(r.raw as RowRaw, r.prior); return !!id && a.optionIds.includes(id); });
      const parts = typeof a.count === 'number' && a.count > 0 ? a.count : new Set(rows.map(r => r.bundleKey || r.key)).size;
      return { n: a.n, label: a.title, parts, description: (a.line || '').trim(), optionIds: a.optionIds };
    }) : (answerSet?.answers || []).map(a => {
      const rows = modalRows.filter(r => { const id = hintIdOf(r.raw as RowRaw, r.prior); return !!id && a.optionIds.includes(id); });
      const parts = new Set(rows.map(r => r.bundleKey || r.key)).size;
      const names = Array.from(new Set(rows.map(r => cleanPartName(r.name)).filter((x): x is string => !!x)));
      return { n: a.n, label: a.label, parts, description: names.slice(0, 3).join(', ') + (names.length > 3 ? '…' : ''), optionIds: a.optionIds };
    }).filter(a => a.parts > 0);
    // Part counts read "1 part" / "N parts", like the chat card.
    return (
      <div data-testid="question-block" style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '16px 20px 12px', background: '#ffffff', minWidth: 0 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {answered ? (
            <span data-testid="question-pill" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: ANSWERED_GREEN, background: '#d8f3ea', borderRadius: 999, padding: '4px 11px' }}>
              <Check size={12} strokeWidth={2.8} aria-hidden /> {tr('chat.clarification.answered', 'Answered')}
            </span>
          ) : (
            <span data-testid="question-pill" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#92610a', background: '#fde68a', borderRadius: 999, padding: '4px 11px' }}>
              <AlertTriangle size={12} strokeWidth={2.4} aria-hidden /> {tr('chat.clarification.needed', 'Clarification needed')}
            </span>
          )}
        </span>
        {label && (
          <div data-testid="part-request" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', minHeight: 30, padding: '8px 14px', backgroundColor: '#f4f4f7', border: '1px solid #ececf1', borderRadius: 10 }}>
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6c6978' }}>
              {tr('chat.clarification.partRequest', 'Part request')}
            </span>
            <strong style={{ flex: '1 1 160px', minWidth: 0, fontSize: 14, color: '#282631', lineHeight: 1.4, overflowWrap: 'anywhere' }}>{label}</strong>
          </div>
        )}
        {sections.length === 1 && !!question.missing?.length && (
          <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#92610a' }}>
            {tOpts('chat.clarification.missing', { defaultValue: 'Missing · {{what}}', what: question.missing.join(' / ') })}
          </span>
        )}
        <h2 style={{ margin: 0, fontSize: 19, fontWeight: 700, color: '#282631', lineHeight: 1.4, minWidth: 0, overflowWrap: 'anywhere' }}>
          {modalQuestion || tr('chat.clarification.diagramTitle', 'Section diagram')}
        </h2>
        {answers.length > 0 && (
          <div data-testid="answer-options" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <button type="button" data-testid="answer-options-toggle" aria-expanded={answersOpen} onClick={() => setAnswersOpen(o => !o)}
              style={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 8, padding: 0, borderWidth: 0, borderStyle: 'none', background: 'transparent', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#6c6978' }}>
              {tr('chat.clarification.answerOptions', 'Answer options')}
              <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 22, height: 20, padding: '0 6px', borderRadius: 999, background: '#f0eff4', color: '#4a4856', fontSize: 11.5, letterSpacing: 0 }}>{answers.length}</span>
              <ChevronDown size={14} aria-hidden style={{ transform: answersOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s ease' }} />
            </button>
            {answersOpen && (
              <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {answers.map(a => {
                  // Picking an answer shows its parts: on the list and the
                  // drawing (the list's own filter). Answers that ARE parts —
                  // nothing to filter by — bring that part into view instead.
                  const on = !!ctx?.answerActive(a.n, a.label);
                  const pick = () => {
                    // Where the answer's OWN parts live. When none of them is on
                    // this drawing, switch to theirs first — a word the answer
                    // shares with stray rows here ("Front" on a rear bumper's
                    // screws) must not pass for the answer (live 2026-10-18).
                    const homes = (question.options || [])
                      .filter(o => (a.optionIds || []).includes(o.id))
                      .flatMap(o => (o.load || []).map(r => `${r.group_id}/${r.section_id}`));
                    const elsewhere = sections.map(keyOf).find(k => k !== modalKey && homes.includes(k));
                    if (elsewhere && !homes.includes(modalKey)) {
                      setActiveSection(elsewhere);
                      setModalKey(elsewhere);
                      setAnswerRequest(prev => ({ n: a.n, label: a.label, sectionKey: elsewhere, nonce: (prev?.nonce ?? 0) + 1 }));
                      return;
                    }
                    // Its parts are on this diagram: filter here.
                    if (ctx?.toggleAnswer(a.n, a.label)) return;
                    // A part itself, nothing to filter by: bring it into view.
                    const first = a.optionIds?.[0];
                    if (first && md) focusSectionOption(modalKey, md, first);
                  };
                  return (
                    <li key={a.n} style={{ listStyle: 'none' }}>
                      <button type="button" data-testid="answer-option" aria-pressed={on} onClick={pick}
                        style={{ display: 'flex', alignItems: 'flex-start', gap: 10, width: '100%', padding: '8px 12px', border: `1px solid ${on ? DIAGRAM_PALETTE.navy : '#ececf1'}`, borderRadius: 10, background: on ? DIAGRAM_PALETTE.selBg : '#fff', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left', boxShadow: on ? `0 0 0 3px ${DIAGRAM_PALETTE.selBg}` : undefined }}>
                        <span aria-hidden style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: 999, background: DIAGRAM_PALETTE.navy, color: '#fff', fontSize: 12, fontWeight: 800 }}>{a.n}</span>
                        <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <span style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                            <strong style={{ fontSize: 14, color: '#282631' }}>{a.label}</strong>
                            <span style={{ fontSize: 12, color: '#6c6978', whiteSpace: 'nowrap' }}>{a.parts === 1 ? tOpts('chat.clarification.onePart', { defaultValue: '1 part' }) : tOpts('chat.clarification.nParts', { defaultValue: '{{count}} parts', count: a.parts })}</span>
                          </span>
                          {a.description && <span style={{ fontSize: 12, color: '#6c6978', lineHeight: 1.4, overflowWrap: 'anywhere' }}>{a.description}</span>}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
        )}
      </div>
    );
  };

  // Footer: Cancel just returns to the form; Confirm ALSO answers the question
  // (Next / Submit). In a multi-question form the map STAYS open on Next — the
  // next question's map takes its place — and only closes on Submit.
  const renderFooter = ({ close }: { close: () => void }) => (
    <>
      <span style={{ flex: '1 1 220px', minWidth: 0, marginRight: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {selectedSummary ? (
          <>
            {/* No "Selected · your choice" caption: the names say it, and
                Confirm carries the count. An answered card still says so. */}
            {answered && (
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: ANSWERED_GREEN }}>
                {tr('chat.clarification.answered', 'Answered')}
              </span>
            )}
            <span title={selectedSummary} style={{ minWidth: 0, maxWidth: 520, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12.5, fontWeight: 700, color: DIAGRAM_PALETTE.textPrimary }}>
              {selectedSummary}
            </span>
          </>
        ) : (
          <span style={{ fontSize: 12, fontWeight: 500, color: DIAGRAM_PALETTE.textMuted }}>
            {tr('chat.clarification.nothingSelected', 'Nothing selected yet — answer above or pick a part number')}
          </span>
        )}
      </span>
      {stepper && (
        <>
          <span style={{ fontSize: 12, fontWeight: 600, color: PRIMARY }}>
            {tOpts('chat.clarification.stepProgress', { defaultValue: 'Question {{current}} of {{total}}', current: stepper.current, total: stepper.total })}
          </span>
          <button
            type="button"
            onClick={stepper.onBack}
            disabled={!stepper.canGoBack}
            style={{ borderRadius: 8, cursor: stepper.canGoBack ? 'pointer' : 'default', background: 'transparent', color: stepper.canGoBack ? PRIMARY : PRIMARY_DISABLED, fontSize: 14, fontWeight: 600, fontFamily: 'Inter Tight, system-ui, sans-serif', border: `1px solid ${stepper.canGoBack ? PRIMARY : PRIMARY_DISABLED}`, padding: '9px 22px' }}
          >{tr('chat.clarification.back', 'Back')}</button>
        </>
      )}
      <button
        type="button"
        onClick={close}
        style={{ borderRadius: 8, cursor: 'pointer', background: 'transparent', color: DIAGRAM_PALETTE.textPrimary, fontSize: 13, fontWeight: 600, fontFamily: 'Inter Tight, system-ui, sans-serif', border: `1px solid ${DIAGRAM_PALETTE.border}`, padding: '9px 16px' }}
      >{answered ? tr('chat.clarification.close', 'Close') : tr('common.cancel', 'Cancel')}</button>
      {!answered && <button
        type="button"
        disabled={!!confirmDisabled}
        onClick={() => {
          if (confirmDisabled) return;
          if (!stepper || stepper.isLast) close();
          onConfirm?.();
        }}
        style={{ border: 'none', borderRadius: 8, cursor: confirmDisabled ? 'default' : 'pointer', background: confirmDisabled ? PRIMARY_DISABLED : PRIMARY, color: '#fff', fontSize: 14, fontWeight: 600, fontFamily: 'Inter Tight, system-ui, sans-serif', padding: '9px 20px', boxShadow: confirmDisabled ? 'none' : '0 2px 10px rgba(104,102,255,0.25)' }}
      >{confirmLabel || tr('chat.clarification.confirm', 'Confirm')} →</button>}
    </>
  );

  return (
    // `translate="no"` / `notranslate`: this subtree renders bilingual text.
    // Browser auto-translate rewriting text nodes under React crashes it.
    <div translate="no" className="notranslate" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {multi ? (
        // Several diagrams answer this question (front vs rear bumper): show
        // them SIDE BY SIDE, two to a row, each a card the user opens on its
        // own section. No tabs — the choice is visual.
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
          {sections.map((sec, i) => {
            const k = keyOf(sec);
            const dk = diagrams[k];
            const unavailable = dk !== undefined && (!dk || !dk.diagram_url);
            // The card is named by the AI. While that name is on its way the pill is
            // just the spinner; the catalog's own section text (and its id) shows
            // only when the AI produced no name at all (finished or failed).
            const aiLabel = areaHint?.area_labels?.[k] || '';
            const tagPending = areaState?.status === 'loading' && !aiLabel;
            const label = aiLabel || (tagPending ? '' : (sectionTabLabel(k) || `${tr('chat.clarification.diagram', 'Diagram')} ${i + 1}`));
            const showSectionId = !aiLabel && !tagPending;
            const candidates = (question.options || []).filter(o => (o.load || []).some(r => `${r.group_id}/${r.section_id}` === k)).length;
            return (
              <div key={k} style={{ display: 'flex', flexDirection: 'column', background: '#fff', border: '1px solid #e3e2e8', borderRadius: 10, overflow: 'hidden', minWidth: 0 }}>
                <div style={{ position: 'relative', height: 170, background: '#fff' }}>
                  {unavailable || !dk?.diagram_url ? (
                    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, color: '#999', fontSize: 11 }}>
                      <ImageOff size={14} /> {tr('chat.clarification.diagramUnavailable', 'Diagram not available for this section.')}
                    </div>
                  ) : (
                    <img src={dk.diagram_url} alt={`Section ${sec.section_id}`} draggable={false} style={{ display: 'block', width: '100%', height: '100%', objectFit: 'contain' }} />
                  )}
                  {!unavailable && (
                    <button
                      type="button"
                      aria-label={`${tr('chat.clarification.openMap', 'Open the diagram to check')} · ${label}`}
                      onClick={() => { setActiveSection(k); openModal(k); }}
                      style={{ position: 'absolute', inset: 0, zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', background: 'transparent', cursor: 'pointer', padding: 0 }}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, borderRadius: 999, background: PRIMARY, color: '#fff', fontSize: 13, fontWeight: 600, padding: '8px 16px', boxShadow: '0 4px 14px rgba(0,0,0,0.28)', maxWidth: '90%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {label}{tagPending && <Loader2 size={13} className="animate-spin" style={{ flexShrink: 0 }} />}
                      </span>
                    </button>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '8px 12px', borderTop: '1px solid #eeedf3', minWidth: 0 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#282631', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
                  {tagPending && <Loader2 size={12} className="animate-spin" style={{ flexShrink: 0, color: '#8a8896' }} />}
                  {showSectionId && <span style={{ fontSize: 11, color: '#8a8896', flexShrink: 0 }}>{sec.section_id}</span>}
                  {candidates > 0 && (
                    <span style={{ marginLeft: 'auto', flexShrink: 0, fontSize: 12, fontWeight: 700, color: PRIMARY }}>
                      {tOpts('chat.clarification.candidatesCount', { defaultValue: '{{count}} candidates', count: candidates })}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (!d || !d.diagram_url) ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#999', padding: 16, justifyContent: 'center', border: '1px dashed #ddd', borderRadius: 10 }}>
          <ImageOff size={16} /> {tr('chat.clarification.diagramUnavailable', 'Diagram not available for this section.')}
        </div>
      ) : (
        // The chat card shows the WHOLE drawing, letterboxed to a fixed height
        // (never cropped), with one action over it. Callouts, the parts list
        // and selection live in the modal.
        <div style={{ position: 'relative', width: '100%', background: '#fff', border: '1px solid #e3e2e8', borderRadius: 10, overflow: 'hidden' }}>
          <button
            type="button"
            aria-label={tr('chat.clarification.openMap', 'Open the diagram to check')}
            onClick={() => openModal(key)}
            style={{ position: 'absolute', inset: 0, zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', background: 'transparent', cursor: 'pointer', padding: 0 }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, borderRadius: 999, background: PRIMARY, color: '#fff', fontSize: 13, fontWeight: 600, padding: '9px 16px', boxShadow: '0 4px 14px rgba(0,0,0,0.28)' }}>
              <Maximize2 size={15} /> {tr('chat.clarification.openMap', 'Open the diagram to check')}
            </span>
          </button>
          <img src={d.diagram_url} alt={`Section ${s.section_id}`} draggable={false} style={{ display: 'block', width: '100%', height: 'auto', maxHeight: 340, objectFit: 'contain', background: '#fff' }} />
        </div>
      )}

      {/* The shared fullscreen viewer. The diagram stays null while the next
          question's section is still loading, so the shell (and the stepper)
          never flashes closed between questions. */}
      <DiagramModal
        open={!!modalKey}
        t={t}
        diagram={md && (md.diagram_url || (md as any).sheets?.length) ? {
          diagram_url: md.diagram_url,
          callouts: md.callouts || [],
          // Pass the sheets through: a section's drawings each carry a
          // different slice of the parts, and narrowing the payload to one
          // image here is what hid the other sheets from the viewer.
          sheets: (md as any).sheets ?? null,
          sheet_count: (md as any).sheet_count ?? null,
          primary_sheet: (md as any).primary_sheet ?? null,
          section_name: (md as any).section_name ?? null,
          group_name: (md as any).group_name ?? null,
        } : null}
        sectionName={(md as any)?.section_name ?? null}
        groupName={(md as any)?.group_name ?? null}
        sectionCode={modalKey ? `${md?.group_id || modalKey.split('/')[0]} / ${md?.section_id || modalKey.split('/')[1]}` : null}
        // Several diagrams: move between them inside the viewer (the outside
        // area cards are not on screen when the map was opened from a button).
        sectionTabs={sections.length > 1 ? sections.map((s, i) => {
          const key = keyOf(s);
          const count = (question.options || []).filter(o => (o.load || []).some(r => `${r.group_id}/${r.section_id}` === key)).length;
          // While the AI is still naming the diagrams the tab holds a spinner
          // (as the cards outside do), so its name never swaps under the user.
          const pending = areaState?.status === 'loading' && !areaHint?.area_labels?.[key];
          return { key, label: areaLabel(key) || (t ? t('chat.clarification.diagramN', { defaultValue: 'Diagram {{n}}', n: String(i + 1) }) : `Diagram ${i + 1}`), count, pending };
        }) : null}
        activeSectionKey={modalKey}
        onSelectSection={(key) => { setActiveSection(key); setModalKey(key); }}
        candidateSections={modalKey ? (candidates[modalKey] ?? null) : null}
        onOpenCandidates={modalKey && md ? () => loadCandidates(modalKey, md.group_id, md.section_id) : undefined}
        onPickCandidate={(c) => { void openCandidate(c as CandidateSection); }}
        rows={modalRows}
        onClose={closeModal}
        onToggleRow={modalKey ? toggleRow(modalKey) : () => {}}
        // Several parts may be needed for one request (both fenders, a cover
        // and its clips), so the viewer always picks with checkboxes.
        selectionMode="multi"
        selectedCount={selectedCount}
        onClearSelection={!answered ? clearSelection : undefined}
        render3d={(url, signal) => chatService.getDiagram3d(url, sessionId, signal)}
        build3dModel={model3d ? (url, parts, signal, rebuild, onProgress) => chatService.buildDiagram3dModel(url, parts, signal, rebuild, onProgress) : null}
        renderEmptyAction={!answered && !disabled && onCantFind ? () => (
          <button type="button" data-testid="cant-find" onClick={() => { closeModal(); onCantFind(); }}
            style={{ borderRadius: 8, cursor: 'pointer', background: DIAGRAM_PALETTE.bgSurface, color: DIAGRAM_PALETTE.textPrimary, fontSize: 12.5, fontWeight: 600, fontFamily: 'inherit', border: `1px solid ${DIAGRAM_PALETTE.border}`, padding: '7px 14px' }}>
            {tr('chat.clarification.cantFindIt', "Can't find it? Describe the part")}
          </button>
        ) : undefined}
        disabled={disabled}
        chooseOneNudge
        requestTexts={[
          question.part_label || '',
          question.part_name || '',
          ...(question.part_parameters || []).map(v => String(v).replace(/^[^:=]+[:=]\s*/, '').trim()),
        ]}
        legendExtras={priorPicks?.length ? [{ kind: 'prior', label: tr('chat.clarification.legend.selectedEarlier', 'Selected earlier') }] : undefined}
        focusRequest={focusRequest}
        renderHeader={renderHeader}
        renderFooter={renderFooter}
        renderOverlay={() => (modalKey && md && modalScope && hintOpenKey === modalKey && modalHintState) ? (
          // Keep the parts list visible while reading the comparison: the hint
          // overlays the diagram; its candidate links scroll + highlight rows
          // (on this drawing or, for a candidate of another, after switching).
          <HintBubble
            t={tOpts}
            question={modalScope.question}
            state={modalHintState}
            title={tr('chat.clarification.whatsDifferent', "What's different?")}
            showIdentical
            placement="overlay"
            onClose={() => setHintOpenKey(null)}
            onFocusOption={(optionId) => focusSectionOption(modalKey, md, optionId)}
          />
        ) : null}
        // The AI comparison lives IN the list: every part row it compared wears
        // its distinguishing tags + "choose when…" (single or nested — the old
        // label-level note left single rows and supersession variants bare),
        // and a strip on top sums it up and opens the full table.
        renderRowInsight={(row, { shownValues, names }) => {
          const optionId = hintIdOf(row.raw as RowRaw, row.prior);
          if (!onRequestHint || !optionId || !modalScope || !modalHintState) return null;
          return <HintCandidateNote t={tOpts} state={modalHintState} question={modalScope.question} optionId={optionId} variant="row" exclude={shownValues} names={names} hideTags />;
        }}
        requestedType={requestedType}
        rowAnswer={rowAnswer}
        rowAiTags={rowAiTags}
        diffDimension={modalHint?.differentiator || null}
        onCompare={modalKey && onRequestHint ? () => setHintOpenKey(modalKey) : undefined}
        // "What's different?" — this diagram's candidates, or across every
        // diagram when this one holds a single candidate (hintScopeFor).
        answerRequest={answerRequest}
        whatsDifferent={modalKey && onRequestHint && modalScope ? {
          onOpen: () => {
            setHintOpenKey(modalKey);
            // A failed comparison is retried from the button.
            if (modalHintState?.status === 'error') hintRequested.current.delete(modalScope.key);
            void requestSectionHint(modalScope.key, modalScope.question, false);
          },
          loading: modalHintState?.status === 'loading',
        } : null}
        rowHasDifference={(row, { names }) => {
          const optionId = hintIdOf(row.raw as RowRaw, row.prior);
          if (!onRequestHint || !optionId || !modalScope) return false;
          // Still comparing: don't claim "no difference" before the AI has answered.
          if (modalHintState?.status === 'loading') return true;
          return rowHasHintDifference(modalHintState, modalScope.question, optionId, tOpts, { names });
        }}
        renderListIntro={() => (onRequestHint && modalKey && modalScope) ? (
          <HintComparisonStrip t={tOpts} state={modalHintState} question={modalScope.question}
            onOpen={() => setHintOpenKey(modalKey)} hideOpen />
        ) : null}
      />
    </div>
  );
};

export default ClarificationDiagramInline;
